import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Pagination,
  Dropdown,
  DropdownButton,
  ProgressBar,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaExchangeAlt,
  FaStore,
  FaStar,
  FaFilter,
  FaSearch,
  FaDownload,
  FaChartLine,
  FaUserCheck,
  FaTruck,
  FaHourglassHalf,
} from "react-icons/fa";
import { format } from "date-fns";

const AssignedBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    category: "",
    search: "",
    vendor: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    assigned: 0,
    accepted: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
  });

  // Fetch assigned bookings
  const fetchAssignedBookings = async () => {
    try {
      setLoading(true);
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      let url = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/assigned?limit=${pagination.limit}`;
      
      // Add status filter to URL if not "all"
      if (filters.status !== "all") {
        url += `&status=${filters.status}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch assigned bookings");
      }

      const data = await response.json();
      let filteredBookings = data.bookings || [];

      // Apply local filters
      if (filters.category) {
        filteredBookings = filteredBookings.filter(
          (b) =>
            b.category?.toLowerCase() === filters.category.toLowerCase() ||
            b.serviceName?.toLowerCase().includes(filters.category.toLowerCase())
        );
      }

      if (filters.vendor) {
        filteredBookings = filteredBookings.filter(
          (b) =>
            b.vendorName?.toLowerCase().includes(filters.vendor.toLowerCase()) ||
            b.vendorId?.toLowerCase().includes(filters.vendor.toLowerCase())
        );
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredBookings = filteredBookings.filter(
          (b) =>
            b.customerName?.toLowerCase().includes(searchLower) ||
            b.serviceName?.toLowerCase().includes(searchLower) ||
            b.customerPhone?.includes(searchLower) ||
            b.vendorName?.toLowerCase().includes(searchLower) ||
            b.id?.toLowerCase().includes(searchLower)
        );
      }

      setBookings(filteredBookings);
      setPagination((prev) => ({ ...prev, total: filteredBookings.length }));
      
      // Calculate stats
      calculateStats(filteredBookings);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching assigned bookings:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (bookingsList) => {
    const newStats = {
      total: bookingsList.length,
      assigned: bookingsList.filter(b => b.status === "assigned").length,
      accepted: bookingsList.filter(b => b.status === "accepted").length,
      inProgress: bookingsList.filter(b => b.status === "in_progress").length,
      completed: bookingsList.filter(b => b.status === "completed").length,
      cancelled: bookingsList.filter(b => b.status === "cancelled").length,
    };
    setStats(newStats);
  };

  // Fetch available vendors for reassignment
  const fetchAvailableVendors = async (category, pincode, excludeVendorId) => {
    try {
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      let url = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/admin/available-vendors`;
      const params = new URLSearchParams();
      if (category) params.append("category", category);
      if (pincode) params.append("pincode", pincode);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch available vendors");
      }

      const data = await response.json();
      // Exclude current vendor from list
      let vendors = data.vendors || [];
      if (excludeVendorId) {
        vendors = vendors.filter(v => v.id !== excludeVendorId);
      }
      setAvailableVendors(vendors);
    } catch (err) {
      console.error("Error fetching available vendors:", err);
      setAvailableVendors([]);
    }
  };

  // Reassign vendor to booking
  const handleReassignVendor = async () => {
    if (!selectedBooking || !selectedVendorId) {
      alert("Please select a vendor");
      return;
    }

    try {
      setReassigning(true);
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${selectedBooking.id}/reassign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ vendorId: selectedVendorId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reassign vendor");
      }

      const result = await response.json();
      alert(`✅ ${result.message}`);

      // Refresh bookings list
      fetchAssignedBookings();
      setShowReassignModal(false);
      setSelectedBooking(null);
      setSelectedVendorId("");
    } catch (err) {
      console.error("Error reassigning vendor:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setReassigning(false);
    }
  };

  // Update booking status
  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/${bookingId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      await fetchAssignedBookings();
      if (showDetailsModal) {
        setShowDetailsModal(false);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update booking status");
    }
  };

  // Open reassign modal
  const openReassignModal = async (booking) => {
    setSelectedBooking(booking);
    setSelectedVendorId("");
    await fetchAvailableVendors(booking.category, booking.pincode, booking.vendorId);
    setShowReassignModal(true);
  };

  // View booking details
  const viewBookingDetails = (booking) => {
    setDetailsBooking(booking);
    setShowDetailsModal(true);
  };

  // Get status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      assigned: { variant: "info", text: "Assigned", icon: <FaClock /> },
      accepted: { variant: "success", text: "Accepted", icon: <FaCheckCircle /> },
      in_progress: { variant: "primary", text: "In Progress", icon: <FaTruck /> },
      completed: { variant: "secondary", text: "Completed", icon: <FaCheckCircle /> },
      cancelled: { variant: "danger", text: "Cancelled", icon: <FaTimesCircle /> },
      rejected: { variant: "warning", text: "Rejected", icon: <FaTimesCircle /> },
    };

    const config = statusConfig[status] || { variant: "secondary", text: status, icon: null };
    return (
      <Badge bg={config.variant} className="px-3 py-2">
        {config.icon && <span className="me-1">{config.icon}</span>}
        {config.text}
      </Badge>
    );
  };

  // Get progress percentage for in-progress bookings
  const getProgressPercentage = (booking) => {
    if (booking.status === "completed") return 100;
    if (booking.status === "in_progress") return 75;
    if (booking.status === "accepted") return 50;
    if (booking.status === "assigned") return 25;
    return 0;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy • h:mm a");
    } catch {
      return dateString;
    }
  };

  // Format scheduled date
  const formatScheduledDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Booking ID", "Customer Name", "Phone", "Service", "Vendor", "Status", "Price", "Created Date", "Scheduled Date"];
    const csvData = bookings.map(booking => [
      booking.id,
      booking.customerName,
      booking.customerPhone,
      booking.serviceName,
      booking.vendorName,
      booking.status,
      booking.price,
      formatDate(booking.createdAt),
      formatScheduledDate(booking.scheduledDate),
    ]);

    const csvContent = [headers, ...csvData].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assigned-bookings-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchAssignedBookings();
    // Refresh every 60 seconds
    const interval = setInterval(fetchAssignedBookings, 60000);
    return () => clearInterval(interval);
  }, [pagination.limit, filters.status]);

  // Refresh when filters change
  useEffect(() => {
    fetchAssignedBookings();
  }, [filters.category, filters.vendor, filters.search]);

  if (loading && bookings.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading assigned bookings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error Loading Bookings</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchAssignedBookings}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="assigned-bookings-container p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <FaUserCheck className="text-success me-2" />
            Assigned Bookings
          </h2>
          <p className="text-muted">
            Track and manage bookings assigned to vendors
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-success" onClick={exportToCSV}>
            <FaDownload className="me-1" />
            Export CSV
          </Button>
          <Button variant="outline-primary" onClick={fetchAssignedBookings}>
            <FaSearch className="me-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h3>{stats.total}</h3>
              <div className="text-muted">Total Bookings</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center bg-info text-white">
            <Card.Body>
              <h3>{stats.assigned}</h3>
              <div>Assigned</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center bg-success text-white">
            <Card.Body>
              <h3>{stats.accepted}</h3>
              <div>Accepted</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center bg-primary text-white">
            <Card.Body>
              <h3>{stats.inProgress}</h3>
              <div>In Progress</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center bg-secondary text-white">
            <Card.Body>
              <h3>{stats.completed}</h3>
              <div>Completed</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center bg-danger text-white">
            <Card.Body>
              <h3>{stats.cancelled}</h3>
              <div>Cancelled</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status Filter</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value, page: 1 })
                  }
                >
                  <option value="all">All Statuses</option>
                  <option value="assigned">Assigned</option>
                  <option value="accepted">Accepted</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Service Category</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by category"
                  value={filters.category}
                  onChange={(e) =>
                    setFilters({ ...filters, category: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Vendor Name/ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by vendor"
                  value={filters.vendor}
                  onChange={(e) =>
                    setFilters({ ...filters, vendor: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Customer, phone, booking ID..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                  />
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Items per page</Form.Label>
                <Form.Select
                  value={pagination.limit}
                  onChange={(e) =>
                    setPagination({ ...pagination, limit: parseInt(e.target.value), page: 1 })
                  }
                  style={{ width: "100px" }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Bookings Table */}
      {bookings.length === 0 ? (
        <Alert variant="info" className="text-center">
          <h5>No assigned bookings found</h5>
          <p className="mb-0">Try adjusting your filters or check back later.</p>
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table striped hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Vendor</th>
                <th>Scheduled</th>
                <th>Price</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.slice(
                (pagination.page - 1) * pagination.limit,
                pagination.page * pagination.limit
              ).map((booking) => (
                <tr key={booking.id}>
                  <td>
                    <code className="small">{booking.id?.slice(-25)}</code>
                  </td>
                  <td>
                    <div className="fw-semibold">{booking.customerName}</div>
                    <small className="text-muted">
                      <FaPhone className="me-1" size={12} />
                      {booking.customerPhone}
                    </small>
                  </td>
                  <td>
                    <div>{booking.serviceName}</div>
                    <small className="text-muted">
                      {booking.category || "No category"}
                    </small>
                  </td>
                  <td>
                    <div>
                      <FaStore className="text-primary me-1" size={12} />
                      {booking.vendorName || "Unknown"}
                    </div>
                    <small className="text-muted">
                      {booking.vendorPhone && (
                        <>
                          <FaPhone className="me-1" size={10} />
                          {booking.vendorPhone}
                        </>
                      )}
                    </small>
                  </td>
                  <td>
                    <div className="small">
                      <FaCalendarAlt className="me-1" size={12} />
                      {formatScheduledDate(booking.scheduledDate)}
                    </div>
                    {booking.scheduledTime && (
                      <small className="text-muted">
                        <FaClock className="me-1" size={10} />
                        {booking.scheduledTime}
                      </small>
                    )}
                  </td>
                  <td>
                    <span className="fw-semibold">₹{booking.price}</span>
                    <br />
                    <Badge bg="light" text="dark" className="small">
                      {booking.paymentMode?.toUpperCase() || "COD"}
                    </Badge>
                  </td>
                  <td>{getStatusBadge(booking.status)}</td>
                  <td style={{ width: "120px" }}>
                    <ProgressBar
                      now={getProgressPercentage(booking)}
                      label={`${getProgressPercentage(booking)}%`}
                      variant={
                        booking.status === "completed" ? "success" :
                        booking.status === "cancelled" ? "danger" : "primary"
                      }
                      size="sm"
                    />
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => viewBookingDetails(booking)}
                        title="View Details"
                      >
                        <FaEye />
                      </Button>
                      {booking.status !== "completed" && booking.status !== "cancelled" && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => openReassignModal(booking)}
                          title="Reassign Vendor"
                        >
                          <FaExchangeAlt />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {bookings.length > pagination.limit && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.Prev
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
              }
              disabled={pagination.page === 1}
            />
            {[...Array(Math.ceil(bookings.length / pagination.limit))].map((_, idx) => (
              <Pagination.Item
                key={idx + 1}
                active={pagination.page === idx + 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: idx + 1 }))}
              >
                {idx + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() =>
                setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
              }
              disabled={
                pagination.page === Math.ceil(bookings.length / pagination.limit)
              }
            />
          </Pagination>
        </div>
      )}

      {/* Reassign Vendor Modal */}
      <Modal show={showReassignModal} onHide={() => setShowReassignModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExchangeAlt className="me-2" />
            Reassign Vendor to Booking
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              <div className="mb-4 p-3 bg-light rounded">
                <h6>Current Booking Details:</h6>
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Service:</strong> {selectedBooking.serviceName}
                    </div>
                    <div className="mb-2">
                      <strong>Customer:</strong> {selectedBooking.customerName}
                    </div>
                    <div className="mb-2">
                      <strong>Phone:</strong> {selectedBooking.customerPhone}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Current Vendor:</strong> {selectedBooking.vendorName}
                    </div>
                    <div className="mb-2">
                      <strong>Status:</strong> {getStatusBadge(selectedBooking.status)}
                    </div>
                    <div className="mb-2">
                      <strong>Created:</strong> {formatDate(selectedBooking.createdAt)}
                    </div>
                  </Col>
                </Row>
              </div>

              <h6>Available Vendors for Reassignment:</h6>
              {availableVendors.length === 0 ? (
                <Alert variant="warning">
                  No available vendors found for this category/pincode.
                  <hr />
                  <small>
                    Ensure other vendors have:
                    <ul className="mt-2 mb-0">
                      <li>Active subscription</li>
                      <li>Security deposit paid</li>
                      <li>Available status enabled</li>
                      <li>Matching service category</li>
                      <li>Capacity to accept more bookings</li>
                    </ul>
                  </small>
                </Alert>
              ) : (
                <div className="vendors-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {availableVendors.map((vendor) => (
                    <Card
                      key={vendor.id}
                      className={`mb-2 cursor-pointer ${
                        selectedVendorId === vendor.id ? "border-primary bg-light" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedVendorId(vendor.id)}
                    >
                      <Card.Body className="py-2">
                        <Row className="align-items-center">
                          <Col md={5}>
                            <div className="fw-semibold">
                              <FaStore className="me-2 text-primary" />
                              {vendor.name}
                            </div>
                            <small className="text-muted">
                              <FaPhone className="me-1" size={12} />
                              {vendor.phone || "No phone"}
                            </small>
                          </Col>
                          <Col md={3}>
                            <div className="text-warning">
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  color={i < Math.floor(vendor.rating || 0) ? "#ffc107" : "#e4e5e9"}
                                  size={14}
                                  className="me-1"
                                />
                              ))}
                              <span className="ms-1 text-muted small">
                                ({vendor.totalReviews || 0})
                              </span>
                            </div>
                          </Col>
                          <Col md={2}>
                            <Badge
                              bg={vendor.canAcceptMore ? "success" : "danger"}
                              className="w-100 text-center"
                            >
                              {vendor.activeBookings || 0} / {vendor.maxBookings || 5} Active
                            </Badge>
                          </Col>
                          <Col md={2} className="text-end">
                            {selectedVendorId === vendor.id && (
                              <FaCheckCircle className="text-success" size={20} />
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReassignModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleReassignVendor}
            disabled={!selectedVendorId || reassigning || availableVendors.length === 0}
          >
            {reassigning ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Reassigning...
              </>
            ) : (
              <>
                <FaExchangeAlt className="me-2" />
                Reassign Vendor
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Booking Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye className="me-2" />
            Booking Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailsBooking && (
            <>
              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">Booking Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Booking ID:</strong> <code>{detailsBooking.id}</code>
                      </div>
                      <div className="mb-2">
                        <strong>Status:</strong> {getStatusBadge(detailsBooking.status)}
                      </div>
                      <div className="mb-2">
                        <strong>Created:</strong> {formatDate(detailsBooking.createdAt)}
                      </div>
                      <div className="mb-2">
                        <strong>Scheduled:</strong> {formatScheduledDate(detailsBooking.scheduledDate)}
                        {detailsBooking.scheduledTime && ` at ${detailsBooking.scheduledTime}`}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">Payment Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Amount:</strong> ₹{detailsBooking.price}
                      </div>
                      <div className="mb-2">
                        <strong>Payment Mode:</strong> {detailsBooking.paymentMode?.toUpperCase() || "COD"}
                      </div>
                      {detailsBooking.paymentStatus && (
                        <div className="mb-2">
                          <strong>Payment Status:</strong>{" "}
                          <Badge bg={detailsBooking.paymentStatus === "paid" ? "success" : "warning"}>
                            {detailsBooking.paymentStatus}
                          </Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <FaUser className="me-2" />
                      Customer Information
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Name:</strong> {detailsBooking.customerName}
                      </div>
                      <div className="mb-2">
                        <strong>Phone:</strong> {detailsBooking.customerPhone}
                      </div>
                      {detailsBooking.customerEmail && (
                        <div className="mb-2">
                          <strong>Email:</strong> {detailsBooking.customerEmail}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Address:</strong> {detailsBooking.address}
                      </div>
                      {detailsBooking.pincode && (
                        <div className="mb-2">
                          <strong>Pincode:</strong> {detailsBooking.pincode}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header className="bg-light">
                      <FaStore className="me-2" />
                      Vendor Information
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Name:</strong> {detailsBooking.vendorName || "Not assigned"}
                      </div>
                      {detailsBooking.vendorPhone && (
                        <div className="mb-2">
                          <strong>Phone:</strong> {detailsBooking.vendorPhone}
                        </div>
                      )}
                      {detailsBooking.vendorEmail && (
                        <div className="mb-2">
                          <strong>Email:</strong> {detailsBooking.vendorEmail}
                        </div>
                      )}
                      {detailsBooking.assignedAt && (
                        <div className="mb-2">
                          <strong>Assigned On:</strong> {formatDate(detailsBooking.assignedAt)}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header className="bg-light">Service Information</Card.Header>
                    <Card.Body>
                      <div className="mb-2">
                        <strong>Service:</strong> {detailsBooking.serviceName}
                      </div>
                      {detailsBooking.category && (
                        <div className="mb-2">
                          <strong>Category:</strong> {detailsBooking.category}
                        </div>
                      )}
                      {detailsBooking.description && (
                        <div className="mb-2">
                          <strong>Description:</strong> {detailsBooking.description}
                        </div>
                      )}
                      {detailsBooking.notes && (
                        <div className="mb-2">
                          <strong>Notes:</strong> {detailsBooking.notes}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Status Update Section */}
              {detailsBooking.status !== "completed" && detailsBooking.status !== "cancelled" && (
                <div className="mt-3">
                  <Card>
                    <Card.Header className="bg-light">Update Status</Card.Header>
                    <Card.Body>
                      <div className="d-flex gap-2 flex-wrap">
                        {["assigned", "accepted", "in_progress", "completed", "cancelled"].map((status) => (
                          <Button
                            key={status}
                            variant={detailsBooking.status === status ? "primary" : "outline-secondary"}
                            size="sm"
                            onClick={() => handleUpdateStatus(detailsBooking.id, status)}
                            disabled={detailsBooking.status === status}
                          >
                            {status.replace("_", " ").toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {detailsBooking && detailsBooking.status !== "completed" && detailsBooking.status !== "cancelled" && (
            <Button
              variant="warning"
              onClick={() => {
                setShowDetailsModal(false);
                openReassignModal(detailsBooking);
              }}
            >
              <FaExchangeAlt className="me-2" />
              Reassign Vendor
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignedBookings;